"use client";

import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from "react";
import { useChat, ChatOptions } from "../hooks/useChat";
import { ChatContextType, ChatConfig } from "../types";

const ChatContext = createContext<ChatContextType | null>(null);

interface ChatContextProviderProps {
  children: ReactNode;
  initialConfig?: ChatConfig;
  initialOptions?: ChatOptions;
}

export const ChatContextProvider: React.FC<ChatContextProviderProps> = ({
  children,
  initialConfig = {},
  initialOptions = {},
}) => {
  const chat = useChat({
    initialMessages: initialConfig.initialMessages || [],
    persistKey: initialConfig.persistKey,
    flows: initialConfig.flows || {},
    initialFlow: initialConfig.initialFlow,
    typingDelay: initialConfig.typingIndicatorTimeout || 1000,
    initiallyOpen: initialConfig.initiallyOpen || false,
    ...initialOptions,
  });

  // We need to implement a proper removeMessage function
  const removeMessage = useCallback(
    (id: string) => {
      // We can use updateMessage to "remove" by filtering out the message in the UI
      // Real removal would require modifying the useChat hook
      chat.updateMessage(id, { text: "", type: "system" });
    },
    [chat]
  );

  // Implement setIsTyping to properly change typing state
  const setIsTyping = useCallback(
    (typing: boolean) => {
      if (typing === chat.isTyping) return; // No change needed

      // Since we don't have direct access to setState in useChat,
      // we'll use a workaround for setting typing state
      if (typing) {
        // Simulate typing start if we need to
        const placeholderMsg = {
          text: "",
          type: "bot" as const,
          isTyping: true,
        };

        // This will trigger the typing indicator indirectly
        const tempMsg = chat.addMessage(placeholderMsg);

        // Clean up after a delay - this is optional
        if (!chat.isTyping) {
          setTimeout(() => {
            // Remove the temporary typing message or replace it with an empty message
            chat.updateMessage(tempMsg.id, { text: "", type: "system" });
          }, initialConfig.typingIndicatorTimeout || 1000);
        }
      }
      // For turning off typing, we don't need to do anything special
      // as the typing indicator is controlled by message state in components
    },
    [chat, initialConfig.typingIndicatorTimeout]
  );

  // Create a value object that fully matches ChatContextType
  const contextValue: ChatContextType = {
    messages: chat.messages,
    isTyping: chat.isTyping,
    isOpen: chat.isOpen,
    isMinimized: chat.isMinimized,
    currentFlow: chat.currentFlow,
    availableOptions: chat.availableOptions,
    config: initialConfig,

    // Methods from useChat
    addMessage: chat.addMessage,
    updateMessage: chat.updateMessage,
    clearMessages: chat.clearMessages,
    toggleChat: chat.toggleChat,
    openChat: chat.openChat,
    closeChat: chat.closeChat,
    minimizeChat: chat.minimizeChat,
    maximizeChat: chat.maximizeChat,
    startFlow: chat.startFlow,
    handleButtonClick: chat.handleButtonClick,

    // Our custom implementations
    removeMessage,
    setIsOpen: (open: boolean) => {
      if (open) chat.openChat();
      else chat.closeChat();
    },
    setIsMinimized: (minimized: boolean) => {
      if (minimized) chat.minimizeChat();
      else chat.maximizeChat();
    },
    setIsTyping,
  };

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};

export const useGlobalChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useGlobalChat must be used within a ChatContextProvider");
  }
  return context;
};

export default ChatContext;
